import React, { useState } from "react";
import iconArrowUp from "../../images/icons/arrow-up.svg";
import iconArrowDown from "../../images/icons/arrow-down.svg";
import iconDelete from "../../images/icons/delete.svg";

const OrderSummary = ({ 
    tickets = [], 
    order = {}, 
    resources = {},
    language = 'de',
    showDeliveryCosts = false,
    showPromotionCodes = false,
    promotionCodes = [],
    onDeleteTicket = null,  // Optional delete handler for Summary page - can accept single ID or array of IDs
    onDeleteTicketGroup = null  // Optional handler specifically for deleting groups
}) => {
    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    const formatDateHeader = (dateStr, timeFrom, timeTo) => {
        const date = new Date(dateStr);

        // Get day name based on language
        const dayNames = {
            'de': ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
            'en': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            'fr': ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
            'it': ['Domenica', 'Luned�', 'Marted�', 'Mercoled�', 'Gioved�', 'Venerd�', 'Sabato']
        };

        const langCode = language || 'de';
        const dayName = dayNames[langCode] ? dayNames[langCode][date.getDay()] : dayNames['de'][date.getDay()];

        // Format date as DD.MM.YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        // Format time from HH:MM:SS to HH:MM
        const formatTime = (time) => {
            if (!time) return '';
            return time.substring(0, 5);
        };

        // If no time range is provided, just show the date
        if (!timeFrom && !timeTo) {
            return `${dayName}, ${day}.${month}.${year}`;
        }

        return `${dayName}, ${day}.${month}.${year} | ${formatTime(timeFrom)} - ${formatTime(timeTo)}`;
    };

    const handleDeleteGroup = (group) => {
        if (!onDeleteTicket && !onDeleteTicketGroup) return;
        
        // Collect all ticket IDs in the group
        const ticketIds = group.tickets.map(ticket => ticket.id);
        
        // Use onDeleteTicketGroup if available, otherwise use onDeleteTicket with array
        if (onDeleteTicketGroup) {
            onDeleteTicketGroup(ticketIds);
        } else if (onDeleteTicket) {
            onDeleteTicket(ticketIds);
        }
    };

    const mapTickets = () => {
        if (!tickets || tickets.length === 0) return null;

        // Separate tickets with and without time ranges
        const ticketsWithTime = tickets.filter(t => t.timeOfEntryFrom && t.timeOfEntryTo);
        const ticketsWithoutTime = tickets.filter(t => !t.timeOfEntryFrom || !t.timeOfEntryTo);

        // Group tickets with time by date and time range - keep individual tickets
        const grouped = ticketsWithTime.reduce((acc, ticket) => {
            const dateKey = ticket.ticketType?.start || '';
            const timeFromKey = ticket.timeOfEntryFrom || '';
            const timeToKey = ticket.timeOfEntryTo || '';
            const key = `${dateKey}|${timeFromKey}|${timeToKey}|${ticket.ticketTypeId}`;

            if (!acc[key]) {
                acc[key] = {
                    tickets: [],
                    ticketType: ticket.ticketType,
                    info: ticket.info,
                    price: ticket.ticketTypePrice,
                    currency: ticket.ticketType?.currency || '',
                    dateKey: dateKey,
                    timeFrom: timeFromKey,
                    timeTo: timeToKey,
                    ticketTypeId: ticket.ticketTypeId
                };
            }
            acc[key].tickets.push(ticket);
            return acc;
        }, {});

        // Group by date+time header for display
        const groupedByDateTime = {};
        Object.entries(grouped).forEach(([key, group]) => {
            const headerKey = `${group.dateKey}|${group.timeFrom}|${group.timeTo}`;
            if (!groupedByDateTime[headerKey]) {
                groupedByDateTime[headerKey] = {
                    header: formatDateHeader(group.dateKey, group.timeFrom, group.timeTo),
                    dateKey: group.dateKey,
                    timeFrom: group.timeFrom,
                    timeTo: group.timeTo,
                    items: []
                };
            }
            groupedByDateTime[headerKey].items.push(group);
        });

        // Sort by date and time to find the first group
        const sortedGroups = Object.values(groupedByDateTime).sort((a, b) => {
            const dateCompare = new Date(a.dateKey) - new Date(b.dateKey);
            if (dateCompare !== 0) return dateCompare;
            return a.timeFrom.localeCompare(b.timeFrom);
        });

        // Add tickets without time to the first group (or create a default group if no groups exist)
        if (ticketsWithoutTime.length > 0) {
            // Group tickets without time by ticketTypeId
            const groupedWithoutTime = ticketsWithoutTime.reduce((acc, ticket) => {
                const key = ticket.ticketTypeId;
                if (!acc[key]) {
                    acc[key] = {
                        tickets: [],
                        ticketType: ticket.ticketType,
                        info: ticket.info,
                        price: ticket.ticketTypePrice,
                        currency: ticket.ticketType?.currency || '',
                        ticketTypeId: ticket.ticketTypeId
                    };
                }
                acc[key].tickets.push(ticket);
                return acc;
            }, {});

            if (sortedGroups.length > 0) {
                // Add them to the first group's items
                const firstGroup = sortedGroups[0];
                Object.values(groupedWithoutTime).forEach(group => {
                    firstGroup.items.push(group);
                });
            } else {
                // Create a default group if no groups exist
                const defaultDateKey = ticketsWithoutTime[0]?.ticketType?.start || new Date().toISOString();
                sortedGroups.push({
                    header: formatDateHeader(defaultDateKey, '', ''),
                    dateKey: defaultDateKey,
                    timeFrom: '',
                    timeTo: '',
                    items: Object.values(groupedWithoutTime)
                });
            }
        }

        return (
            <>
                {sortedGroups.map((dateTimeGroup, dateIndex) => (
                    <div key={dateIndex} className="mb-1">
                        {/* Date/Time Header */}
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <b>{dateTimeGroup.header}</b>
                            </div>
                        </div>

                        {/* Column Headers */}
                        <div className="row mb-2">
                            <div className="col-md-6 col-7">
                                <b>{resources?.Ticket || 'Ticket'}</b>
                            </div>
                            <div className="col-md-3 col-3 d-none d-sm-block">
                                <b>{resources?.TicketPrice || 'Ticketpreis'}</b>
                            </div>
                            <div className="col-md-2 col-4 text-end">
                                <b>{resources?._Total}</b>
                            </div>
                            {onDeleteTicket && (
                                <div className="col-md-1 col-1"></div>
                            )}
                        </div>

                        {/* Ticket Groups */}
                        {dateTimeGroup.items.map((group, itemIndex) => {
                            const groupKey = `${dateIndex}-${itemIndex}`;
                            const isExpanded = expandedGroups[groupKey];
                            const count = group.tickets.length;
                            const totalPrice = count * group.price;

                            return (
                                <div key={itemIndex} className="mb-2">
                                    {/* Collapsed Row */}
                                    <div className="row py-2 align-items-center">
                                        <div 
                                            className="col-md-6 col-7 d-flex align-items-center"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => toggleGroup(groupKey)}
                                        >
                                            <img 
                                                src={isExpanded ? iconArrowUp : iconArrowDown} 
                                                alt="" 
                                                style={{ marginRight: '10px', width: '16px', height: '16px' }} 
                                            />
                                            <span>{group.info?.name}</span>
                                        </div>
                                        <div className="col-md-3 col-3 d-none d-sm-block">
                                            <span>{count} x {group.currency} {group.price.toFixed(2)}</span>
                                        </div>
                                        <div className="col-md-2 col-4 text-end">
                                            <span>{group.currency} {totalPrice.toFixed(2)}</span>
                                        </div>
                                        {onDeleteTicket && (
                                            <div className="col-md-1 col-1 text-end">
                                                <img 
                                                    src={iconDelete}
                                                    alt="Delete"
                                                    style={{ cursor: 'pointer', width: '25px', height: '25px' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteGroup(group);
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Expanded Rows */}
                                    {isExpanded && (
                                        <div className="ms-4">
                                            {group.tickets.map((ticket, ticketIdx) => (
                                                <div
                                                    key={ticketIdx}
                                                    className="row py-2 align-items-center"
                                                >
                                                    <div className="col-md-6 col-7">
                                                        <div className="ms-4">
                                                            <span>{group.info?.name}</span>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-3 col-3 d-none d-sm-block">
                                                        <span>{group.currency} {group.price.toFixed(2)}</span>
                                                    </div>
                                                    <div className="col-md-2 col-4 text-end">
                                                        {/* Empty space - no price shown for individual tickets */}
                                                    </div>
                                                    {onDeleteTicket && (
                                                        <div className="col-md-1 col-1 text-end">
                                                            <img 
                                                                src={iconDelete}
                                                                alt="Delete"
                                                                style={{ cursor: 'pointer', width: '25px', height: '25px' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDeleteTicket(ticket.id);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </>
        );
    };

    return (
        <div>
            {/* Ticket Summary */}
            <div className="row">
                <div className="col-md-12">
                    {mapTickets()}
                </div>
            </div>

            {/* Separator */}
            <div className="row">
                <div className="col-md-12">
                    <hr className="ticket-separator" />
                </div>
            </div>

            {/* Extra Costs (Delivery/Payment Fee) */}
            {showDeliveryCosts && order?.deliverPaymentFee > 0 && (
                <div className="row pt-2 pb-1">
                    <div className="col-md-8 col-8">
                        <label>{resources?.DeliveryCosts || 'Zustellkosten'}</label>
                    </div>
                    <div className="col-md-4 col-4 text-end">
                        <label>{order.currency} {order?.deliverPaymentFee.toFixed(2)}</label>
                    </div>
                </div>
            )}

            {/* Promotion Codes */}
            {showPromotionCodes && promotionCodes && promotionCodes.length > 0 && (
                <div>
                    {promotionCodes.map((code, index) => (
                        <div className="row" key={index}>
                            <div className="col-md-12 text-success">
                                {code} (Promotion code)
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Total */}
            <div className="row">
                <div className="col-md-12">
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center flex-nowrap">
                            <strong>{resources?._TotalInclTaxes}</strong>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <label className="text-nowrap">
                                {order && order.totalPrice !== null && order.totalPrice !== undefined ? (
                                    <strong>{order.currency} {(order.totalPrice).toFixed(2)}</strong>
                                ) : (
                                    <strong>CHF 0.00</strong>
                                )}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderSummary;
